<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { STATE_MENU_ADDRESS_SYSTEM } from '@/constants/statesOfAddressSystem'

import { useAutocompleteAddress } from '@/composables/address-system/useAutocompleteAddress'

import {
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'

import type { SuggestedOfAddress } from '@/api/modules/geo/geocode.types'

const AddressSystemMenuError = defineAsyncComponent(
  () => import('@/components/AddressSystem/menu/AddressSystemMenuError.vue'),
)

const AddressSystemMenuSelected = defineAsyncComponent(
  () => import('@/components/AddressSystem/menu/AddressSystemMenuSelected.vue'),
)

const AddressSystemMenuSuggestions = defineAsyncComponent(
  () =>
    import('@/components/AddressSystem/menu/AddressSystemMenuSuggestions.vue'),
)

const AddressSystemMenuNotDelivery = defineAsyncComponent(
  () =>
    import('@/components/AddressSystem/menu/AddressSystemMenuNotDelivery.vue'),
)

const AddressSystemMenuNotGeo = defineAsyncComponent(
  () => import('~/components/AddressSystem/menu/AddressSystemMenuNotGeo.vue'),
)

type Props = {
  loading: boolean
  suggestions: SuggestedOfAddress[]
}

const props = defineProps<Props>()

const emit = defineEmits([
  'on-update-field-address',
  'on-update-address-info',
  'on-update-menu-suggestions',
])

const { $metrics } = useNuxtApp()
const { isMobile } = useResponsive()

const { deliveryAddress } = storeToRefs(useAddressSystemStore())

const { menuState } = storeToRefs(useAddressSystemStateStore())
const { changeMenuState } = useAddressSystemStateStore()

const { getCoordinatesByAddress } = useAutocompleteAddress()

const isVisibleAppProgress = computed(() => {
  return props.loading || menuState.value === STATE_MENU_ADDRESS_SYSTEM.LOADING
})

const isVisibleMenuSelected = computed(() => {
  return (
    menuState.value === STATE_MENU_ADDRESS_SYSTEM.SELECTED &&
    deliveryAddress.value.details &&
    !isMobile.value
  )
})

const isVisibleMenuNotDelivery = computed(() => {
  return (
    menuState.value === STATE_MENU_ADDRESS_SYSTEM.NOT_DELIVERY &&
    !isMobile.value
  )
})

// Получение координат по выбранному адресу
const onUpdateSelectedAddress = async (
  selectedSuggestion: SuggestedOfAddress,
) => {
  changeMenuState(STATE_MENU_ADDRESS_SYSTEM.LOADING)

  emit('on-update-field-address', selectedSuggestion.value)

  const coordinates = await getCoordinatesByAddress(selectedSuggestion)

  if (!coordinates) return

  emit('on-update-address-info', { selectedSuggestion, coordinates })

  $metrics?.eventSendGoal({ ym: 'address_suggestion_clicked' })
}

const onUpdateMenuSuggestions = (suggest: SuggestedOfAddress) => {
  emit('on-update-menu-suggestions', suggest)
}

onUnmounted(() => changeMenuState(STATE_MENU_ADDRESS_SYSTEM.DEFAULT))
</script>

<template>
  <the-loader
    v-if="isVisibleAppProgress"
    class="menu-loading"
    color="var(--ui-dark-grey)"
  />

  <address-system-menu-error
    v-else-if="menuState === STATE_MENU_ADDRESS_SYSTEM.ERROR"
    class="menu-error"
  />

  <address-system-menu-suggestions
    v-else-if="menuState === STATE_MENU_ADDRESS_SYSTEM.SUGGESTIONS"
    :suggestions="suggestions"
    class="menu-suggestions"
    @update:selected-address="onUpdateSelectedAddress"
    @on-update-menu-suggestions="onUpdateMenuSuggestions"
  />

  <address-system-menu-selected
    v-else-if="isVisibleMenuSelected"
    :address-info="deliveryAddress.details"
    class="menu-selected"
  />

  <address-system-menu-not-geo
    v-else-if="menuState === STATE_MENU_ADDRESS_SYSTEM.NOT_GEO"
  />

  <address-system-menu-not-delivery
    v-else-if="isVisibleMenuNotDelivery"
    class="menu-not-delivery"
  />
</template>

<style scoped lang="scss">
.menu {
  &-default,
  &-error,
  &-selected,
  &-not-delivery,
  &-loading {
    width: 100%;
    height: 210px !important;
    overflow-y: hidden;

    @include media('>=portrait-phone') {
      height: auto;
    }
  }
}
</style>
